import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { api, type UserResponse, type AuthStatusCheckResponse } from '../lib/api';

interface AuthContextType {
  user: UserResponse | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  tradingMode: string | undefined;
  checkAuth: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [tradingMode, setTradingMode] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const status: AuthStatusCheckResponse = await api.getMe();
      if (status.authenticated && status.user) {
        setUser(status.user);
        setTradingMode(status.trading_mode);
      } else {
        setUser(null);
        setTradingMode(undefined);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setTradingMode(undefined);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
      setUser(null);
      setTradingMode(undefined);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'ADMIN',
    tradingMode,
    checkAuth,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
